import ClapprScrubThumbnails from 'clappr-thumbnails-plugin'
import {Utils, type AttributeMap, type SupportedVersion} from '@clappr/core'
import {parseTracks} from 'utils/vtt'
import {getVideoTextTracks, ThumbnailsConfig} from 'common/api/records'
import type {Options} from 'common/types/options'
import type {MediaControl} from 'common/plugins/media-control'
import {Events} from 'common/types/events'
import './styles.css'

export class Thumbnails<T extends Options> extends ClapprScrubThumbnails<T> {
  private static isVertical = false

  private $time!: ZeptoCollection
  private $thumb!: ZeptoCollection
  private $episodeLabel!: ZeptoCollection
  private seekDragging = false
  private seekHover = false

  public get name(): string {
    return 'scrub-thumbnails'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get attributes(): AttributeMap {
    return {}
  }

  public get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  public static async preload(options: Options): Promise<void> {
    const {thumbnails, episodes} = options

    if (!thumbnails?.sprite || !thumbnails?.url) {
      if (episodes) return
      throw new Error('thumbnails sprite or url is missed')
    }

    const tracks = await getVideoTextTracks(thumbnails.url)
    const thumbs = parseTracks(tracks)

    if (!thumbs?.length) {
      throw new Error('thumbnails tracks are missed')
    }

    let thumbWidth
    let thumbHeight
    let timeInterval
    const colsNum = thumbs
      .filter((thumb) => thumb.start !== thumb.end)
      .reduce((result, thumb, index) => {
        const [, top, w, h] = thumb.text
          .split('#xywh=')[1]
          .split(',')
          .map(Number)

        if (index === 0) {
          thumbWidth = w
          thumbHeight = h
          timeInterval = thumb.end - thumb.start
        }

        return top === 0 ? result + 1 : result
      }, 0)

    Thumbnails.isVertical =
      thumbHeight != null && thumbWidth != null && thumbHeight > thumbWidth

    if (thumbWidth && thumbHeight && timeInterval) {
      thumbnails.thumbs = Thumbnails.buildSpriteConfig(
        thumbnails.sprite,
        thumbs.length,
        thumbWidth,
        thumbHeight,
        colsNum,
        timeInterval
      )
    }

    if (!thumbnails?.thumbs) {
      throw new Error('thumbnails thumbs is missed')
    }
  }

  public bindEvents(): void {
    super.bindEvents()
    this.listenTo(
      this.core.mediaControl,
      Events.MEDIACONTROL_SEEK_START,
      this.onSeekStart
    )
    this.listenTo(this.core.mediaControl, Events.MEDIACONTROL_SEEK, this.onSeek)
    this.listenTo(
      this.core.mediaControl,
      Events.MEDIACONTROL_SEEK_END,
      this.onSeekEnd
    )
  }

  protected _getOptions(): ThumbnailsConfig {
    const {thumbnails} = this.core.options

    if (!thumbnails) {
      throw new Error('thumbnails property missing from options object')
    }

    return thumbnails
  }

  private onSeekStart(ev: MouseEvent | TouchEvent): void {
    this.seekDragging = true
    super._onMouseMove(ev)
  }

  private onSeek(ev: MouseEvent | TouchEvent): void {
    super._onMouseMove(ev)
  }

  private onSeekEnd(): void {
    this.seekDragging = false
    if (this.seekHover) return

    super._onMouseLeave()
  }

  public _onMouseMove(ev: MouseEvent | TouchEvent): void {
    this.seekHover = true
    if (this.seekDragging) return

    super._onMouseMove(ev)
  }

  public _onMouseLeave(): void {
    this.seekHover = false
    if (this.seekDragging) return

    super._onMouseLeave()
  }

  public _calculateHoverPosition(e: MouseEvent | TouchEvent): void {
    const pageX = 'targetTouches' in e ? e.targetTouches[0].pageX : e.pageX
    const offset =
      pageX - this.core.mediaControl.$seekBarContainer.offset().left

    // proportion into the seek bar that the mouse is hovered over 0-1
    this._hoverPosition = Math.min(
      1,
      Math.max(offset / this.core.mediaControl.$seekBarContainer.width(), 0)
    )
  }

  public _updateSpotlightThumb(): void {
    if (!this.mediaControl) return
    const {episodes} = this.core.options as any

    const hoverPosition = this._hoverPosition
    const videoDuration = this.core.activeContainer.getDuration()
    // the time into the video at the current hover position
    const startTimeOffset = this.core.activeContainer.getStartTimeOffset()
    const hoverTime = startTimeOffset + videoDuration * hoverPosition
    let episodeLabel

    if (episodes && this.$episodeLabel.length) {
      for (let i = 0; i < episodes.length; i++) {
        const item = episodes[i]

        if (item.start <= hoverTime && hoverTime < item.end) {
          episodeLabel = item.label
          break
        }
      }

      episodeLabel ??= episodes[episodes.length - 1].label
    }

    if (this.$thumb?.length && this._thumbs.length) {
      // determine which thumbnail applies to the current time
      const thumbIndex = this._getThumbIndexForTime(hoverTime)
      const thumb = this._thumbs[thumbIndex]

      // update thumbnail
      this.$thumb.css({
        '--thumbnails-image-ratio': `${thumb.w / thumb.h}`,
        background: 'no-repeat',
        backgroundImage: `url('${thumb.url}')`,
        backgroundPositionX: -thumb.x,
        backgroundPositionY: -thumb.y,
        width: thumb.w,
        height: thumb.h
      })
    }

    // update seek time
    this.$time?.text(Utils.formatTime(hoverTime))
    this.$episodeLabel.text(episodeLabel ?? '')

    const mediaControlWidth = this.mediaControl.$el.width()
    const {width: seekBarWidth, left: seekBarLeft} =
      this.mediaControl.$seekBarContainer.offset()
    const thumbWidth = this.$el.width()
    const sideGap = 5

    this.$el.css({
      left: Math.min(
        Math.max(
          sideGap - seekBarLeft,
          seekBarWidth * hoverPosition - thumbWidth / 2
        ),
        mediaControlWidth - thumbWidth - sideGap
      ),
      '--thumbnails-position': seekBarLeft + seekBarWidth * hoverPosition
    })
  }

  public _renderPlugin(): void {
    if (!this._thumbsLoaded) {
      return
    }

    if (this._show && (this._thumbs.length > 0 || this.core.options.episodes)) {
      this.$el.addClass('eplayer-time-visible')
      this._updateSpotlightThumb()
    } else {
      this.$el.removeClass('eplayer-time-visible')
    }
  }

  public _createElements(): void {
    const $template = this.mediaControl?.$el.find('[data-thumbnails]')

    if (!$template?.length) {
      return
    }

    this.$el
      .html($template.html())
      .attr('class', $template.attr('class'))
      .toggleClass('eplayer-thumbnails-vertical', Thumbnails.isVertical)
      .toggleClass('eplayer-thumbnails-without-preview', !this._thumbs.length)
    $template.replaceWith(this.el)

    this.$time = this.$el.find('[data-thumbnails-time], .eplayer-tooltip-time')
    this.$thumb = this.$el.find(
      '[data-thumbnails-sprite], .eplayer-sprite-tooltip'
    )
    this.$episodeLabel = this.$el.find('[data-episode-label]')
  }
}
